import 'braid-design-system/reset';

import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import loadable from 'sku/@loadable/component';

import { ClientOnly } from './components/ClientOnly/ClientOnly';
import { CustomQueryParamProvider } from './components/CustomQueryParam/CustomQueryParam';
import {
  LocalizationProvider,
  type SupportedLocale,
} from './components/LocalizationProvider/LocalizationProvider';
import { PreflightProvider } from './components/PreflightProvider';
import { ProfileAnalyticsProvider } from './components/ProfileAnalyticsProvider/ProfileAnalyticsProvider';
import { UserProvider } from './components/UserProvider';
import { ConsentPage } from './pages/ConsentPage';
import type { ClientContext } from './types/sku';

const SUPPORTED_LOCALES: SupportedLocale[] = [
  { extends: 'en', name: 'en-AU' },
  { extends: 'id', name: 'id-ID' },
  { extends: 'th', name: 'th-TH' },
];

// Split out pages that won't be loaded by prod users.
// istanbul ignore next
const DebugPage = loadable(() => import('./pages/DebugPage'), {
  resolveComponent: (components) => components.DebugPage,
});

const MainPage = () => (
  <CustomQueryParamProvider>
    <UserProvider>
      <PreflightProvider>
        <ProfileAnalyticsProvider>
          <ConsentPage />
        </ProfileAnalyticsProvider>
      </PreflightProvider>
    </UserProvider>
  </CustomQueryParamProvider>
);

// Additional routes when running locally
// istanbul ignore next
const LocalRouter = () => (
  <Routes>
    <Route element={<DebugPage />} path="/oauth2/debug/" />
    <Route element={<MainPage />} path="/api/iam/oauth2/authorize" />
  </Routes>
);

export const ProdRouter = () => (
  <Routes>
    <Route element={<MainPage />} path="/api/iam/oauth2/authorize" />
  </Routes>
);

// Tests use their own wrapper around `ProdRouter`.
// istanbul ignore next
export const App = ({ environment }: Pick<ClientContext, 'environment'>) => (
  <LocalizationProvider supportedLocales={SUPPORTED_LOCALES}>
    <BraidProvider theme={seekJobs}>
      <ToastProvider>
        <ClientOnly>
          {environment === 'local' ? <LocalRouter /> : <ProdRouter />}
        </ClientOnly>
      </ToastProvider>
    </BraidProvider>
  </LocalizationProvider>
);
